import { useState, useEffect, useMemo, useCallback } from "react";
import { Routes, Route, Link, useNavigate, useParams } from "react-router-dom";
import MainLayout from "@/components/layout/MainLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { NativeSelect } from "@/components/ui/native-select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { 
  Plus, ArrowLeft, Edit, Trash2, Search, 
  Wrench, MapPin, ClipboardList, Eye,
  Building, ArrowRightLeft, AlertTriangle, Settings,
  Package, Users, Calendar, DollarSign, XCircle,
  CheckCircle, Clock, AlertOctagon, FileText
} from "lucide-react";
import { ListChecks } from "lucide-react";
import { extractErrorMessage } from "@/lib/errorMessage";
import { CategoriaSelect, CamposExtras, LocalizacaoInicial, F1_DEFAULTS } from "@/components/patrimonio/CamposFase1";
import {
  gerarPDFPatrimonioPorCliente,
  gerarPDFPatrimonioPorFuncionario,
  gerarPDFPatrimonioItem,
} from "@/lib/patrimonioPDF";

function formatCurrency(value) {
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value || 0);
}

function formatDate(dateStr) {
  if (!dateStr) return "-";
  return new Date(dateStr).toLocaleDateString('pt-BR');
}

function formatDateTime(dateStr) {
  if (!dateStr) return "-";
  return new Date(dateStr).toLocaleDateString('pt-BR', { 
    day: '2-digit', month: '2-digit', year: 'numeric', 
    hour: '2-digit', minute: '2-digit' 
  });
}

// Configuração visual das situações
const situacaoConfig = {
  disponivel: { label: "Disponível", class: "bg-green-100 text-green-700", icon: CheckCircle },
  em_obra: { label: "Em Obra", class: "bg-blue-100 text-blue-700", icon: Building },
  em_posse_funcionario: { label: "Com Funcionário", class: "bg-indigo-100 text-indigo-700", icon: Users },
  emprestado: { label: "Emprestado", class: "bg-purple-100 text-purple-700", icon: Users },
  manutencao: { label: "Manutenção", class: "bg-amber-100 text-amber-700", icon: Settings },
  reparo_externo: { label: "Reparo Externo", class: "bg-orange-100 text-orange-700", icon: Settings },
  roubo: { label: "Roubo", class: "bg-red-100 text-red-700", icon: AlertOctagon },
  furto: { label: "Furto", class: "bg-red-100 text-red-700", icon: AlertOctagon },
  perda: { label: "Perda", class: "bg-red-100 text-red-700", icon: XCircle },
  inoperante: { label: "Inoperante", class: "bg-gray-100 text-gray-700", icon: XCircle },
  baixado: { label: "Baixado", class: "bg-slate-100 text-slate-700", icon: Trash2 },
};

const estadoConfig = {
  novo: { label: "Novo", class: "bg-green-100 text-green-700" },
  bom: { label: "Bom", class: "bg-blue-100 text-blue-700" },
  regular: { label: "Regular", class: "bg-amber-100 text-amber-700" },
  ruim: { label: "Ruim", class: "bg-red-100 text-red-700" },
};

const categoriaConfig = {
  ferramenta: { label: "Ferramenta", class: "bg-purple-100 text-purple-700" },
  equipamento: { label: "Equipamento", class: "bg-indigo-100 text-indigo-700" },
};

// ==================== LIST ====================
function PatrimonioList() {
  const [items, setItems] = useState([]);
  const [alertas, setAlertas] = useState([]);
  const [resumoFinanceiro, setResumoFinanceiro] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterSituacao, setFilterSituacao] = useState("all");
  const [filterCategoria, setFilterCategoria] = useState("all");
  const [incluirBaixados, setIncluirBaixados] = useState(false);
  const [incluirInativos, setIncluirInativos] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [showRelatorioClienteDialog, setShowRelatorioClienteDialog] = useState(false);
  const [showRelatorioFuncionarioDialog, setShowRelatorioFuncionarioDialog] = useState(false);
  const [porLocalizacao, setPorLocalizacao] = useState([]);
  const [alertasAtualizacao, setAlertasAtualizacao] = useState([]);
  const [deleteCandidate, setDeleteCandidate] = useState(null);
  const [inativarItem, setInativarItem] = useState(null);
  const [clientes, setClientes] = useState([]);
  const [funcionarios, setFuncionarios] = useState([]);
  const navigate = useNavigate();

  const fetchData = useCallback(async () => {
    try {
      const token = sessionStorage.getItem("luvcorp_token");
      const headers = { Authorization: `Bearer ${token}` };
      
      const [itemsRes, locRes, alertasRes, resumoRes, alertaUpdRes, clientesRes, funcRes] = await Promise.all([
        axios.get(`${API}/patrimonio?incluir_baixados=${incluirBaixados}&incluir_inativos=${incluirInativos}`, { headers }),
        axios.get(`${API}/patrimonio/por-localizacao`, { headers }),
        axios.get(`${API}/patrimonio/alertas`, { headers }),
        axios.get(`${API}/patrimonio/resumo-financeiro`, { headers }),
        axios.get(`${API}/patrimonio/alertas/atualizacao`, { headers }),
        axios.get(`${API}/clientes-fornecedores?tipo=cliente&ativo=true`, { headers }),
        axios.get(`${API}/funcionarios?ativo=true`, { headers }),
      ]);
      
      setItems(itemsRes.data);
      setPorLocalizacao(locRes.data);
      setAlertas(alertasRes.data);
      setResumoFinanceiro(resumoRes.data);
      setAlertasAtualizacao(alertaUpdRes.data?.itens || []);
      setClientes(clientesRes.data || []);
      setFuncionarios(funcRes.data || []);
    } catch (err) {
      toast.error("Erro ao carregar patrimônio");
    } finally {
      setLoading(false);
    }
  }, [incluirBaixados, incluirInativos]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const filteredItems = useMemo(() => {
    return items.filter(item => {
      const matchesSearch = !searchTerm || 
        item.nome?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.codigo_patrimonio?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.marca?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.localizacao_descricao?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.cliente_nome?.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesSituacao = filterSituacao === "all" || item.situacao === filterSituacao;
      const matchesCategoria = filterCategoria === "all" || item.categoria === filterCategoria;
      return matchesSearch && matchesSituacao && matchesCategoria;
    });
  }, [items, searchTerm, filterSituacao, filterCategoria]);

  const headers = () => ({ Authorization: `Bearer ${sessionStorage.getItem("luvcorp_token")}` });

  const handleCheckin = async (item) => {
    try {
      await axios.post(`${API}/patrimonio/${item.id}/atualizar-status`, { confirma_localizacao: true }, { headers: headers() });
      toast.success(`Check-in confirmado: ${item.codigo_patrimonio}`);
      fetchData();
    } catch (err) {
      toast.error(extractErrorMessage(err, "Erro ao confirmar status"));
    }
  };

  const handleReativar = async (item) => {
    if (!confirm(`Reativar "${item.nome}"?`)) return;
    try {
      await axios.post(`${API}/patrimonio/${item.id}/reativar`, {}, { headers: headers() });
      toast.success("Patrimônio reativado");
      fetchData();
    } catch (err) {
      toast.error(extractErrorMessage(err, "Erro ao reativar"));
    }
  };

  const handleDelete = async (id) => {
    // Mantido por compatibilidade — agora a deleção é feita pelo Dialog em deleteCandidate
    setDeleteCandidate(items.find((i) => i.id === id));
  };

  if (loading) return <MainLayout><div className="p-8 text-center">Carregando...</div></MainLayout>;

  return (
    <MainLayout>
      <div className="space-y-6" data-testid="patrimonio-list">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-heading font-bold text-luvcorp-navy">Patrimônio (Equipamentos)</h1>
            <p className="text-slate-500">Ferramentas e equipamentos com rastreabilidade completa</p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button variant="outline" onClick={() => window.location.assign("/cadastros/patrimonio-organizar")} data-testid="btn-organizar-f1">
              <ListChecks className="h-4 w-4 mr-2" /> Organizar (Fase 1)
            </Button>
            <Button variant="outline" onClick={() => setShowRelatorioClienteDialog(true)} data-testid="btn-relatorio-cliente">
              <FileText className="h-4 w-4 mr-2" /> Relatório por Cliente
            </Button>
            <Button variant="outline" onClick={() => setShowRelatorioFuncionarioDialog(true)} data-testid="btn-relatorio-funcionario">
              <FileText className="h-4 w-4 mr-2" /> Relatório por Funcionário
            </Button>
            <Button onClick={() => { setEditingItem(null); setShowForm(true); }} className="bg-luvcorp-gold hover:bg-luvcorp-gold-dark text-white">
              <Plus className="h-4 w-4 mr-2" />
              Novo Item
            </Button>
          </div>
        </div>

        {/* Alertas */}
        {alertas.length > 0 && (
          <Card className="border-0 shadow-sm border-l-4 border-l-amber-500">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <AlertTriangle className="h-5 w-5 text-amber-500" />
                <div>
                  <p className="font-medium text-amber-700">{alertas.length} alertas ativos</p>
                  <p className="text-sm text-slate-500">
                    {alertas.slice(0, 3).map(a => a.mensagem).join(" | ")}
                    {alertas.length > 3 && ` e mais ${alertas.length - 3}...`}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* KPIs */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <Card className="border-0 shadow-sm">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg"><Wrench className="h-5 w-5 text-blue-600" /></div>
                <div>
                  <p className="text-sm text-slate-500">Itens Ativos</p>
                  <p className="text-2xl font-bold text-luvcorp-navy">{resumoFinanceiro?.patrimonio_ativo?.quantidade || 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-sm">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-100 rounded-lg"><DollarSign className="h-5 w-5 text-green-600" /></div>
                <div>
                  <p className="text-sm text-slate-500">Valor Ativo</p>
                  <p className="text-lg font-bold text-green-600">
                    {formatCurrency(resumoFinanceiro?.patrimonio_ativo?.valor_total || 0)}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-sm">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg"><Building className="h-5 w-5 text-blue-600" /></div>
                <div>
                  <p className="text-sm text-slate-500">Em Obra</p>
                  <p className="text-2xl font-bold text-blue-600">
                    {items.filter(i => i.situacao === "em_obra").length}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-sm">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-amber-100 rounded-lg"><Settings className="h-5 w-5 text-amber-600" /></div>
                <div>
                  <p className="text-sm text-slate-500">Em Manutenção</p>
                  <p className="text-2xl font-bold text-amber-600">
                    {items.filter(i => ["manutencao", "reparo_externo"].includes(i.situacao)).length}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-sm">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-red-100 rounded-lg"><AlertOctagon className="h-5 w-5 text-red-600" /></div>
                <div>
                  <p className="text-sm text-slate-500">Prejuízo Total</p>
                  <p className="text-lg font-bold text-red-600">
                    {formatCurrency(resumoFinanceiro?.prejuizo_total || 0)}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        <Tabs defaultValue="lista" className="space-y-4">
          <TabsList>
            <TabsTrigger value="lista">Lista Completa</TabsTrigger>
            <TabsTrigger value="situacao">Por Situação</TabsTrigger>
            <TabsTrigger value="atualizacao" data-testid="tab-atualizacao">
              Alertas de Atualização
              {alertasAtualizacao.length > 0 && (
                <span className="ml-1 text-[10px] bg-red-600 text-white rounded-full px-1.5 py-0.5">{alertasAtualizacao.length}</span>
              )}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="lista" className="space-y-4">
            {/* Filters */}
            <Card className="border-0 shadow-sm">
              <CardContent className="p-4">
                <div className="flex flex-wrap gap-4">
                  <div className="relative flex-1 min-w-[200px]">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <Input
                      placeholder="Buscar por nome, código, marca, localização..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-10"
                    />
                  </div>
                  <NativeSelect
                    value={filterCategoria}
                    onValueChange={setFilterCategoria}
                    options={[
                      { value: "all", label: "Todas Categorias" },
                      { value: "ferramenta", label: "Ferramentas" },
                      { value: "equipamento", label: "Equipamentos" }
                    ]}
                    className="w-[180px]"
                  />
                  <NativeSelect
                    value={filterSituacao}
                    onValueChange={setFilterSituacao}
                    options={[
                      { value: "all", label: "Todas Situações" },
                      { value: "disponivel", label: "Disponível" },
                      { value: "em_obra", label: "Em Obra" },
                      { value: "em_posse_funcionario", label: "Com Funcionário" },
                      { value: "emprestado", label: "Emprestado" },
                      { value: "manutencao", label: "Manutenção" },
                      { value: "reparo_externo", label: "Reparo Externo" }
                    ]}
                    className="w-[180px]"
                  />
                  <label className="flex items-center gap-2 text-sm">
                    <input 
                      type="checkbox" 
                      checked={incluirBaixados} 
                      onChange={(e) => setIncluirBaixados(e.target.checked)}
                      className="rounded"
                    />
                    Incluir baixados/prejuízos
                  </label>
                  <label className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={incluirInativos}
                      onChange={(e) => setIncluirInativos(e.target.checked)}
                      className="rounded"
                      data-testid="filtro-inativos"
                    />
                    Incluir inativos
                  </label>
                </div>
              </CardContent>
            </Card>

            {/* Table */}
            <Card className="border-0 shadow-sm">
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Código</TableHead>
                      <TableHead>Nome</TableHead>
                      <TableHead>Situação</TableHead>
                      <TableHead>Localização</TableHead>
                      <TableHead>Cliente/Responsável</TableHead>
                      <TableHead>Estado</TableHead>
                      <TableHead className="text-right">Valor</TableHead>
                      <TableHead className="text-right">Ações</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredItems.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={8} className="text-center py-8 text-slate-500">
                          Nenhum item encontrado
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredItems.map((item) => {
                        const sit = situacaoConfig[item.situacao] || situacaoConfig.disponivel;
                        const estado = estadoConfig[item.estado_conservacao] || estadoConfig.bom;
                        const cat = categoriaConfig[item.categoria] || categoriaConfig.ferramenta;
                        const SitIcon = sit.icon;
                        return (
                          <TableRow key={item.id} className={item.baixado ? "opacity-60" : ""}>
                            <TableCell className="font-mono font-medium">{item.codigo_patrimonio}</TableCell>
                            <TableCell>
                              <div>
                                <span className="font-medium">{item.nome}</span>
                                <Badge className={`${cat.class} ml-2`} variant="outline">{cat.label}</Badge>
                              </div>
                              {item.marca && <p className="text-xs text-slate-500">{item.marca} {item.modelo}</p>}
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center gap-1">
                                <SitIcon className="h-4 w-4" />
                                <Badge className={sit.class}>{sit.label}</Badge>
                              </div>
                            </TableCell>
                            <TableCell>
                              <span className="text-sm">{item.localizacao_descricao || "-"}</span>
                            </TableCell>
                            <TableCell>
                              {item.cliente_nome && (
                                <div className="text-sm">
                                  <span className="font-medium">{item.cliente_nome}</span>
                                  {item.ordem_servico && <span className="text-xs text-slate-500 block">OS: {item.ordem_servico.numero}</span>}
                                </div>
                              )}
                              {item.emprestado_para && <span className="text-sm text-purple-600">{item.emprestado_para}</span>}
                              {item.responsavel_nome && <span className="text-sm text-slate-500">{item.responsavel_nome}</span>}
                              {!item.cliente_nome && !item.emprestado_para && !item.responsavel_nome && "-"}
                            </TableCell>
                            <TableCell><Badge className={estado.class}>{estado.label}</Badge></TableCell>
                            <TableCell className="text-right font-medium">
                              {formatCurrency(item.valor_atual || item.valor_aquisicao)}
                            </TableCell>
                            <TableCell className="text-right">
                              <div className="flex justify-end gap-1">
                                <Button variant="ghost" size="sm" title="Detalhes" onClick={() => navigate(`/cadastros/patrimonio/${item.id}`)} data-testid={`btn-detalhes-${item.id}`}>
                                  <Eye className="h-4 w-4" />
                                </Button>
                                {!item.baixado && (
                                  <>
                                    <Button variant="ghost" size="sm" title="Editar" onClick={() => { setEditingItem(item); setShowForm(true); }}>
                                      <Edit className="h-4 w-4" />
                                    </Button>
                                    <Button variant="ghost" size="sm" title="Check-in semanal" onClick={() => handleCheckin(item)} data-testid={`btn-checkin-${item.id}`}>
                                      <CheckCircle className="h-4 w-4 text-emerald-600" />
                                    </Button>
                                    {item.ativo !== false ? (
                                      <Button variant="ghost" size="sm" title="Inativar" onClick={() => setInativarItem(item)} data-testid={`btn-inativar-${item.id}`}>
                                        <AlertOctagon className="h-4 w-4 text-amber-600" />
                                      </Button>
                                    ) : (
                                      <Button variant="ghost" size="sm" title="Reativar" onClick={() => handleReativar(item)} data-testid={`btn-reativar-${item.id}`}>
                                        <CheckCircle className="h-4 w-4 text-blue-600" />
                                      </Button>
                                    )}
                                    <Button variant="ghost" size="sm" title="Excluir" onClick={() => setDeleteCandidate(item)} data-testid={`btn-deletar-${item.id}`}>
                                      <Trash2 className="h-4 w-4 text-red-500" />
                                    </Button>
                                  </>
                                )}
                              </div>
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="situacao" className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {porLocalizacao.map((grupo) => {
                const sit = situacaoConfig[grupo._id] || { label: grupo._id, class: "bg-slate-100 text-slate-700" };
                return (
                  <Card key={grupo._id} className="border-0 shadow-sm">
                    <CardHeader className="pb-2">
                      <CardTitle className="flex items-center justify-between text-base">
                        <Badge className={sit.class}>{sit.label}</Badge>
                        <div className="text-right">
                          <span className="text-2xl font-bold">{grupo.quantidade}</span>
                          <p className="text-xs text-slate-500">{formatCurrency(grupo.valor_total)}</p>
                        </div>
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2 max-h-48 overflow-y-auto">
                        {grupo.itens.map((item) => (
                          <div key={item.id} className="flex items-center justify-between p-2 bg-slate-50 rounded text-sm">
                            <div className="flex-1 min-w-0">
                              <span className="font-mono text-xs text-slate-500">{item.codigo}</span>
                              <p className="font-medium truncate">{item.nome}</p>
                              {item.cliente_nome && (
                                <p className="text-xs text-blue-600 truncate">{item.cliente_nome}</p>
                              )}
                              {item.localizacao_descricao && (
                                <p className="text-xs text-slate-500 truncate">{item.localizacao_descricao}</p>
                              )}
                            </div>
                            <Button variant="ghost" size="sm" onClick={() => navigate(`/cadastros/patrimonio/${item.id}`)}>
                              <Eye className="h-4 w-4" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </TabsContent>

          <TabsContent value="atualizacao" className="space-y-3">
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <h3 className="font-semibold flex items-center gap-2">
                      <AlertTriangle className="h-5 w-5 text-amber-500" /> Patrimônios sem atualização há 7+ dias
                    </h3>
                    <p className="text-xs text-slate-500 mt-1">
                      Itens em uso (em obra, emprestado, manutenção) que precisam de confirmação periódica de status/localização.
                    </p>
                  </div>
                  <Badge className="bg-amber-100 text-amber-800">
                    {alertasAtualizacao.length} {alertasAtualizacao.length === 1 ? "alerta" : "alertas"}
                  </Badge>
                </div>
                {alertasAtualizacao.length === 0 ? (
                  <p className="text-center text-emerald-600 py-8 flex items-center justify-center gap-2">
                    <CheckCircle className="h-5 w-5" /> Todos os patrimônios em uso estão atualizados!
                  </p>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Código</TableHead>
                        <TableHead>Nome</TableHead>
                        <TableHead>Situação</TableHead>
                        <TableHead>Localização atual</TableHead>
                        <TableHead>Responsável</TableHead>
                        <TableHead className="text-right">Dias s/ update</TableHead>
                        <TableHead className="text-right">Ações</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {alertasAtualizacao.map((it) => (
                        <TableRow key={it.id} className="bg-amber-50/50" data-testid={`alert-row-${it.id}`}>
                          <TableCell className="font-mono">{it.codigo_patrimonio}</TableCell>
                          <TableCell>{it.nome}</TableCell>
                          <TableCell>
                            <Badge className={situacaoConfig[it.situacao]?.class || ""}>
                              {situacaoConfig[it.situacao]?.label || it.situacao}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-xs">{it.localizacao_descricao || "—"}</TableCell>
                          <TableCell className="text-xs">{it.responsavel_nome || "—"}</TableCell>
                          <TableCell className="text-right">
                            <Badge className={it.dias_sem_atualizar > 14 ? "bg-red-600 text-white" : "bg-amber-200 text-amber-900"}>
                              {it.dias_sem_atualizar !== null ? `${it.dias_sem_atualizar}d` : "—"}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right">
                            <Button size="sm" variant="outline" onClick={() => handleCheckin(it)} data-testid={`btn-checkin-alert-${it.id}`}>
                              <CheckCircle className="h-3.5 w-3.5 mr-1" /> Confirmar
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Form Dialog */}
        <PatrimonioFormDialog 
          open={showForm} 
          onClose={() => setShowForm(false)} 
          item={editingItem}
          onSaved={fetchData}
        />

        {/* DELETE Dialog — bloqueia se em uso e oferece "forçar com senha admin" */}
        <DeletePatrimonioDialog
          item={deleteCandidate}
          onClose={() => setDeleteCandidate(null)}
          onDeleted={fetchData}
        />

        {/* INATIVAR Dialog */}
        <InativarPatrimonioDialog
          item={inativarItem}
          onClose={() => setInativarItem(null)}
          onSaved={fetchData}
        />

        {/* RELATÓRIO POR CLIENTE */}
        <RelatorioClienteDialog
          open={showRelatorioClienteDialog}
          onClose={() => setShowRelatorioClienteDialog(false)}
          clientes={clientes}
        />

        {/* RELATÓRIO POR FUNCIONÁRIO */}
        <RelatorioFuncionarioDialog
          open={showRelatorioFuncionarioDialog}
          onClose={() => setShowRelatorioFuncionarioDialog(false)}
          funcionarios={funcionarios}
        />
      </div>
    </MainLayout>
  );
}

// ==================== FORM DIALOG ====================
function PatrimonioFormDialog({ open, onClose, item, onSaved }) {
  const isEdit = !!item;
  const [saving, setSaving] = useState(false);
  const [camposExtras, setCamposExtras] = useState([]);
  const [formData, setFormData] = useState({
    codigo_patrimonio: "",
    nome: "",
    descricao: "",
    categoria: "ferramenta",
    marca: "",
    modelo: "",
    numero_serie: "",
    proxima_inspecao: "",
    validade_fabricante: "",
    data_aquisicao: "",
    valor_aquisicao: 0,
    estado_conservacao: "bom",
    observacoes: "",
    ...F1_DEFAULTS
  });

  useEffect(() => {
    if (item) {
      setFormData({
        codigo_patrimonio: item.codigo_patrimonio || "",
        nome: item.nome || "",
        descricao: item.descricao || "",
        categoria: item.categoria || "ferramenta",
        marca: item.marca || "",
        modelo: item.modelo || "",
        numero_serie: item.numero_serie || "",
        proxima_inspecao: item.proxima_inspecao || "",
        validade_fabricante: item.validade_fabricante || "",
        data_aquisicao: item.data_aquisicao?.split("T")[0] || "",
        valor_aquisicao: item.valor_aquisicao || 0,
        estado_conservacao: item.estado_conservacao || "bom",
        observacoes: item.observacoes || "",
        ...F1_DEFAULTS,
        categoria_nova: item.categoria_nova || "Ferramentas",
        voltagem: item.voltagem || "", horimetro: item.horimetro || "",
        numero_ca: item.numero_ca || "", validade_ca: item.validade_ca || "",
        data_ultima_inspecao: item.data_ultima_inspecao || "", imei: item.imei || "",
        local_fixo: item.local_fixo || ""
      });
    } else {
      setFormData({
        codigo_patrimonio: "",
        nome: "",
        descricao: "",
        categoria: "ferramenta",
        marca: "",
        modelo: "",
        numero_serie: "",
    proxima_inspecao: "",
    validade_fabricante: "",
        data_aquisicao: new Date().toISOString().split('T')[0],
        valor_aquisicao: 0,
        estado_conservacao: "novo",
        observacoes: "",
        ...F1_DEFAULTS
      });
    }
  }, [item, open]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.codigo_patrimonio || !formData.nome) {
      toast.error("Preencha código e nome");
      return;
    }
    if (!isEdit && !formData.localizacao_tipo) {
      toast.error("Informe a Localização inicial (onde o item está agora)");
      return;
    }

    setSaving(true);
    try {
      const token = sessionStorage.getItem("luvcorp_token");
      const headers = { Authorization: `Bearer ${token}` };
      
      if (isEdit) {
        const { codigo_patrimonio, categoria, data_aquisicao, valor_aquisicao, ...updateData } = formData;
        await axios.put(`${API}/patrimonio/${item.id}`, updateData, { headers });
        toast.success("Item atualizado");
      } else {
        await axios.post(`${API}/patrimonio`, formData, { headers });
        toast.success("Item cadastrado");
      }
      onSaved();
      onClose();
    } catch (err) {
      toast.error(extractErrorMessage(err, "Erro ao salvar"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Editar Item" : "Novo Item do Patrimônio"}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Código Patrimônio *</Label>
              <Input 
                value={formData.codigo_patrimonio} 
                onChange={(e) => setFormData({...formData, codigo_patrimonio: e.target.value.toUpperCase()})} 
                placeholder="FERR-001"
                disabled={isEdit}
              />
            </div>
            <div className="space-y-2">
              <Label>Categoria *</Label>
              <CategoriaSelect
                value={formData.categoria_nova}
                onChange={(nome, extras) => { setFormData({ ...formData, categoria_nova: nome }); setCamposExtras(extras); }}
              />
            </div>
          </div>
          <CamposExtras camposExtras={camposExtras} formData={formData} setFormData={setFormData} />
          <div className="space-y-2">
            <Label>Nome *</Label>
            <Input 
              value={formData.nome} 
              onChange={(e) => setFormData({...formData, nome: e.target.value})} 
              placeholder="Ex: Martelo de Unha" 
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Marca</Label>
              <Input 
                value={formData.marca} 
                onChange={(e) => setFormData({...formData, marca: e.target.value})} 
                placeholder="Tramontina" 
              />
            </div>
            <div className="space-y-2">
              <Label>Modelo</Label>
              <Input 
                value={formData.modelo} 
                onChange={(e) => setFormData({...formData, modelo: e.target.value})} 
                placeholder="PRO-500" 
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label>Número de Série {formData.categoria_nova === "Equipamentos de segurança" && <span className="text-red-500">*</span>}</Label>
            <Input 
              value={formData.numero_serie} 
              onChange={(e) => setFormData({...formData, numero_serie: e.target.value})} 
              placeholder="SN12345" 
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Próxima inspeção (NR-35)</Label>
              <Input type="date" data-testid="input-proxima-inspecao"
                value={formData.proxima_inspecao}
                onChange={(e) => setFormData({...formData, proxima_inspecao: e.target.value})} />
            </div>
            <div className="space-y-2">
              <Label>Validade/vida útil do fabricante</Label>
              <Input type="date" data-testid="input-validade-fabricante"
                value={formData.validade_fabricante}
                onChange={(e) => setFormData({...formData, validade_fabricante: e.target.value})} />
            </div>
          </div>
          <div className="grid grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label>Data Aquisição</Label>
              <Input 
                type="date"
                value={formData.data_aquisicao} 
                onChange={(e) => setFormData({...formData, data_aquisicao: e.target.value})}
                disabled={isEdit}
              />
            </div>
            <div className="space-y-2">
              <Label>Valor Aquisição</Label>
              <Input 
                type="number"
                value={formData.valor_aquisicao} 
                onChange={(e) => setFormData({...formData, valor_aquisicao: parseFloat(e.target.value) || 0})}
                step="0.01"
                disabled={isEdit}
              />
            </div>
            <div className="space-y-2">
              <Label>Estado</Label>
              <NativeSelect
                value={formData.estado_conservacao}
                onValueChange={(v) => setFormData({...formData, estado_conservacao: v})}
                options={[
                  { value: "novo", label: "Novo" },
                  { value: "bom", label: "Bom" },
                  { value: "regular", label: "Regular" },
                  { value: "ruim", label: "Ruim" }
                ]}
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label>Observações</Label>
            <Textarea 
              value={formData.observacoes} 
              onChange={(e) => setFormData({...formData, observacoes: e.target.value})} 
              placeholder="Observações sobre o item" 
              rows={2}
            />
          </div>
          {!isEdit && <LocalizacaoInicial formData={formData} setFormData={setFormData} />}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Cancelar</Button>
            <Button type="submit" disabled={saving} className="bg-luvcorp-navy hover:bg-luvcorp-navy-dark">
              {saving ? "Salvando..." : "Salvar"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ==================== VIEW ====================
function PatrimonioView() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [item, setItem] = useState(null);
  const [clientes, setClientes] = useState([]);
  const [funcionarios, setFuncionarios] = useState([]);
  const [usuarios, setUsuarios] = useState([]);
  const [fornecedores, setFornecedores] = useState([]);
  const [ordensServico, setOrdensServico] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showMovDialog, setShowMovDialog] = useState(false);
  const [showReparoDialog, setShowReparoDialog] = useState(false);
  const [timeline, setTimeline] = useState([]);
  const [reparos, setReparos] = useState([]);
  const [reparosTotalCusto, setReparosTotalCusto] = useState(0);

  const fetchData = useCallback(async () => {
    try {
      const token = sessionStorage.getItem("luvcorp_token");
      const headers = { Authorization: `Bearer ${token}` };
      
      const [itemRes, clientesRes, funcRes, osRes, usersRes, forRes, timelineRes, reparosRes] = await Promise.all([
        axios.get(`${API}/patrimonio/${id}`, { headers }),
        axios.get(`${API}/clientes-fornecedores?tipo=cliente`, { headers }),
        axios.get(`${API}/funcionarios`, { headers }),
        axios.get(`${API}/service-orders?status=backlog,planned,in_progress`, { headers }),
        axios.get(`${API}/users`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/clientes-fornecedores?tipo=fornecedor`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/patrimonio/${id}/timeline`, { headers }).catch(() => ({ data: { eventos: [] } })),
        axios.get(`${API}/patrimonio/${id}/reparos`, { headers }).catch(() => ({ data: { reparos: [], custo_total: 0 } })),
      ]);
      
      setItem(itemRes.data);
      setClientes(clientesRes.data || []);
      setFuncionarios(funcRes.data || []);
      setOrdensServico(osRes.data || []);
      setUsuarios(usersRes.data || []);
      setFornecedores(forRes.data || []);
      setTimeline(timelineRes.data?.eventos || []);
      setReparos(reparosRes.data?.reparos || []);
      setReparosTotalCusto(reparosRes.data?.custo_total || 0);
    } catch (err) {
      toast.error("Erro ao carregar item");
      navigate("/cadastros/patrimonio");
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading) return <MainLayout><div className="p-8 text-center">Carregando...</div></MainLayout>;
  if (!item) return null;

  const sit = situacaoConfig[item.situacao] || situacaoConfig.disponivel;
  const estado = estadoConfig[item.estado_conservacao] || estadoConfig.bom;
  const cat = categoriaConfig[item.categoria] || categoriaConfig.ferramenta;
  const SitIcon = sit.icon;
  const isPrejuizo = ["roubo", "furto", "perda", "inoperante"].includes(item.situacao);

  return (
    <MainLayout>
      <div className="space-y-6" data-testid="patrimonio-view">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/cadastros/patrimonio">
              <Button variant="ghost" size="sm"><ArrowLeft className="h-4 w-4 mr-2" />Voltar</Button>
            </Link>
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-2xl font-heading font-bold text-luvcorp-navy">{item.nome}</h1>
                <Badge className={cat.class}>{cat.label}</Badge>
              </div>
              <p className="text-slate-500 font-mono">{item.codigo_patrimonio}</p>
            </div>
          </div>
          {!item.baixado && (
            <div className="flex gap-2 flex-wrap">
              <Button
                variant="outline"
                onClick={async () => {
                  try {
                    toast.info("Gerando ficha PDF...");
                    await gerarPDFPatrimonioItem({
                      item,
                      historico: item.historico || [],
                      reparos: item.reparos || [],
                    });
                    toast.success("Ficha PDF gerada");
                  } catch (err) { toast.error("Erro ao gerar PDF"); }
                }}
                data-testid="btn-baixar-ficha-pdf"
              >
                <FileText className="h-4 w-4 mr-2" /> Baixar Ficha PDF
              </Button>
              <Button variant="outline" onClick={() => setShowReparoDialog(true)} data-testid="btn-registrar-reparo">
                <Wrench className="h-4 w-4 mr-2" /> Registrar Reparo
              </Button>
              <Button onClick={() => setShowMovDialog(true)} className="bg-luvcorp-gold hover:bg-luvcorp-gold-dark text-white">
                <ArrowRightLeft className="h-4 w-4 mr-2" /> Movimentar
              </Button>
            </div>
          )}
        </div>

        {/* Status atual destacado */}
        <Card className={`border-0 shadow-sm ${isPrejuizo ? "border-l-4 border-l-red-500" : ""}`}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className={`p-3 rounded-lg ${sit.class.replace('text-', 'bg-').split(' ')[0]}`}>
                  <SitIcon className={`h-6 w-6 ${sit.class.split(' ')[1]}`} />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Situação Atual</p>
                  <Badge className={`${sit.class} text-lg px-3 py-1`}>{sit.label}</Badge>
                </div>
              </div>
              <div className="text-right">
                <p className="text-sm text-slate-500">Localização</p>
                <p className="font-medium">{item.localizacao_descricao || "Depósito Central"}</p>
                {item.cliente_nome && <p className="text-sm text-blue-600">{item.cliente_nome}</p>}
                {item.funcionario_nome && <p className="text-sm text-indigo-600">Funcionário: {item.funcionario_nome}{item.funcionario_cargo ? ` — ${item.funcionario_cargo}` : ''}</p>}
                {item.emprestado_para && <p className="text-sm text-purple-600">Emprestado: {item.emprestado_para}</p>}
                {item.ordem_servico && <p className="text-xs text-slate-500">OS: {item.ordem_servico.numero}</p>}
              </div>
            </div>
            {item.data_previsao_retorno && (
              <div className="mt-3 p-2 bg-amber-50 rounded flex items-center gap-2">
                <Clock className="h-4 w-4 text-amber-600" />
                <span className="text-sm text-amber-700">Previsão de retorno: {formatDate(item.data_previsao_retorno)}</span>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Detalhes */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="border-0 shadow-sm">
            <CardContent className="p-4">
              <p className="text-sm text-slate-500">Estado de Conservação</p>
              <Badge className={`${estado.class} mt-1`}>{estado.label}</Badge>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-sm">
            <CardContent className="p-4">
              <p className="text-sm text-slate-500">Marca / Modelo</p>
              <p className="font-medium">{item.marca || "-"}</p>
              <p className="text-sm text-slate-500">{item.modelo || ""}</p>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-sm">
            <CardContent className="p-4">
              <p className="text-sm text-slate-500">Valor Aquisição</p>
              <p className="text-lg font-bold text-green-600">{formatCurrency(item.valor_aquisicao)}</p>
              <p className="text-xs text-slate-500">{formatDate(item.data_aquisicao)}</p>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-sm">
            <CardContent className="p-4">
              <p className="text-sm text-slate-500">Valor Atual</p>
              <p className={`text-lg font-bold ${item.valor_atual > 0 ? "text-blue-600" : "text-red-600"}`}>
                {formatCurrency(item.valor_atual)}
              </p>
              {isPrejuizo && <p className="text-xs text-red-500">Baixado por {sit.label}</p>}
            </CardContent>
          </Card>
        </div>

        {item.responsavel_nome && (
          <Card className="border-0 shadow-sm">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <Users className="h-5 w-5 text-slate-400" />
                <div>
                  <p className="text-sm text-slate-500">Responsável</p>
                  <p className="font-medium">{item.responsavel_nome}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {item.descricao && (
          <Card className="border-0 shadow-sm">
            <CardContent className="p-4">
              <p className="text-sm text-slate-500">Descrição</p>
              <p className="mt-1">{item.descricao}</p>
            </CardContent>
          </Card>
        )}

        {/* Histórico */}
        <Card className="border-0 shadow-sm">
          <CardHeader><CardTitle>Histórico de Movimentações</CardTitle></CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Data</TableHead>
                  <TableHead>Movimentação</TableHead>
                  <TableHead>Localização</TableHead>
                  <TableHead>Cliente/Destino</TableHead>
                  <TableHead>Motivo</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(item.historico || []).length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center py-8 text-slate-500">
                      Nenhuma movimentação registrada
                    </TableCell>
                  </TableRow>
                ) : (
                  item.historico.map((mov) => {
                    const sitNova = situacaoConfig[mov.situacao_nova] || { label: mov.situacao_nova, class: "bg-slate-100" };
                    return (
                      <TableRow key={mov.id}>
                        <TableCell>{formatDateTime(mov.created_at)}</TableCell>
                        <TableCell>
                          <Badge className={sitNova.class}>{sitNova.label}</Badge>
                          {mov.valor_prejuizo && (
                            <span className="text-xs text-red-600 block">Prejuízo: {formatCurrency(mov.valor_prejuizo)}</span>
                          )}
                        </TableCell>
                        <TableCell>{mov.localizacao_descricao || "-"}</TableCell>
                        <TableCell>
                          {mov.cliente_info && <span className="text-sm">{mov.cliente_info.nome}</span>}
                          {mov.emprestado_para && <span className="text-sm text-purple-600">{mov.emprestado_para}</span>}
                          {mov.ordem_servico_info && (
                            <span className="text-xs text-slate-500 block">OS: {mov.ordem_servico_info.numero}</span>
                          )}
                          {!mov.cliente_info && !mov.emprestado_para && "-"}
                        </TableCell>
                        <TableCell>{mov.motivo || "-"}</TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {/* Histórico de Reparos */}
        <Card className="border-0 shadow-sm">
          <CardHeader className="flex flex-row items-center justify-between space-y-0">
            <CardTitle className="flex items-center gap-2">
              <Wrench className="h-5 w-5 text-amber-600" /> Histórico de Reparos
            </CardTitle>
            <Badge variant="outline">Custo total: <span className="ml-1 font-mono">{formatCurrency(reparosTotalCusto)}</span></Badge>
          </CardHeader>
          <CardContent>
            {reparos.length === 0 ? (
              <p className="text-center py-6 text-sm text-slate-500">Nenhum reparo registrado.</p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Início</TableHead>
                    <TableHead>Fim</TableHead>
                    <TableHead>Descrição</TableHead>
                    <TableHead>Fornecedor</TableHead>
                    <TableHead>Responsável</TableHead>
                    <TableHead className="text-right">Custo</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {reparos.map((r) => (
                    <TableRow key={r.id} data-testid={`reparo-row-${r.id}`}>
                      <TableCell>{formatDate(r.data_inicio)}</TableCell>
                      <TableCell>{r.data_fim ? formatDate(r.data_fim) : <Badge variant="outline" className="text-xs">em andamento</Badge>}</TableCell>
                      <TableCell className="max-w-[280px]" title={r.descricao}>{r.descricao}</TableCell>
                      <TableCell className="text-xs">{r.fornecedor_nome || "—"}</TableCell>
                      <TableCell className="text-xs">{r.responsavel_nome || "—"}</TableCell>
                      <TableCell className="text-right font-mono">{formatCurrency(r.custo)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* Linha do Tempo Unificada */}
        <Card className="border-0 shadow-sm">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Clock className="h-5 w-5 text-blue-500" /> Linha do tempo completa
            </CardTitle>
          </CardHeader>
          <CardContent>
            {timeline.length === 0 ? (
              <p className="text-center py-6 text-sm text-slate-500">Nenhum evento registrado.</p>
            ) : (
              <ol className="relative border-l border-slate-200 pl-4 space-y-3">
                {timeline.map((ev, i) => {
                  const d = ev.data || {};
                  const kindMov = ev.kind === "movimentacao";
                  const dotColor = kindMov ? "bg-blue-500" : "bg-amber-500";
                  return (
                    <li key={`${ev.kind}-${i}`} className="ml-2">
                      <span className={`absolute -left-1.5 w-3 h-3 rounded-full ${dotColor}`} />
                      <div className="text-xs text-slate-500">{formatDateTime(ev.ts)}</div>
                      <div className="text-sm">
                        {kindMov ? (
                          <>
                            <Badge className="mr-2 text-[10px]">{d.tipo}</Badge>
                            {d.localizacao_origem && d.localizacao_destino && (
                              <span className="text-slate-600">{d.localizacao_origem} → <b>{d.localizacao_destino}</b></span>
                            )}
                            {d.motivo && <p className="text-xs text-slate-500 mt-0.5">{d.motivo}</p>}
                            {(d.solicitante_info || d.recebedor_info) && (
                              <p className="text-[11px] text-slate-500">
                                {d.solicitante_info && <>Solic.: <b>{d.solicitante_info.name}</b></>}
                                {d.recebedor_info && <> · Receb.: <b>{d.recebedor_info.name}</b></>}
                              </p>
                            )}
                            {d.forced && <Badge variant="destructive" className="text-[9px] mt-1">EXCLUSÃO FORÇADA</Badge>}
                          </>
                        ) : (
                          <>
                            <Badge className="mr-2 bg-amber-100 text-amber-800 text-[10px]">reparo</Badge>
                            <span>{d.descricao}</span>
                            <p className="text-xs text-slate-500">
                              {d.fornecedor_nome ? `${d.fornecedor_nome} · ` : ""}
                              <span className="font-mono">{formatCurrency(d.custo || 0)}</span>
                            </p>
                          </>
                        )}
                      </div>
                    </li>
                  );
                })}
              </ol>
            )}
          </CardContent>
        </Card>

        {/* Movimentação Dialog */}
        <MovimentacaoDialog 
          open={showMovDialog}
          onClose={() => setShowMovDialog(false)}
          item={item}
          clientes={clientes}
          funcionarios={funcionarios}
          ordensServico={ordensServico}
          usuarios={usuarios}
          onSaved={fetchData}
        />

        {/* Reparo Dialog */}
        <ReparoDialog
          open={showReparoDialog}
          onClose={() => setShowReparoDialog(false)}
          item={item}
          fornecedores={fornecedores}
          usuarios={usuarios}
          onSaved={fetchData}
        />
      </div>
    </MainLayout>
  );
}

// ==================== MOVIMENTACAO DIALOG ====================
function MovimentacaoDialog({ open, onClose, item, clientes, funcionarios, ordensServico, usuarios = [], onSaved }) {
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    tipo: "disponivel",
    cliente_id: "",
    funcionario_id: "",
    ordem_servico_id: "",
    localizacao_descricao: "",
    responsavel_id: "",
    solicitante_id: "",
    recebedor_id: "",
    emprestado_para: "",
    motivo: "",
    data_previsao_retorno: ""
  });

  // Situações que este item pode assumir
  const situacoesDisponiveis = [
    { value: "disponivel", label: "Devolver ao Depósito", icon: "🏠" },
    { value: "em_obra", label: "Alocar em Obra de Cliente", icon: "🏗️" },
    { value: "em_posse_funcionario", label: "Alocar a Funcionário", icon: "👤" },
    { value: "emprestado", label: "Emprestar a Terceiro", icon: "🤝" },
    { value: "manutencao", label: "Enviar para Manutenção", icon: "🔧" },
    { value: "reparo_externo", label: "Enviar para Reparo Externo", icon: "🏭" },
    { value: "roubo", label: "Registrar Roubo (Prejuízo)", icon: "🚨" },
    { value: "furto", label: "Registrar Furto (Prejuízo)", icon: "⚠️" },
    { value: "perda", label: "Registrar Perda (Prejuízo)", icon: "❌" },
    { value: "inoperante", label: "Fora de Funcionamento (Prejuízo)", icon: "🚫" },
    { value: "baixado", label: "Dar Baixa (Normal)", icon: "📦" },
  ];

  const isPrejuizo = ["roubo", "furto", "perda", "inoperante"].includes(formData.tipo);
  const requiresCliente = formData.tipo === "em_obra";
  const requiresFuncionarioDestino = formData.tipo === "em_posse_funcionario";
  const requiresEmprestado = formData.tipo === "emprestado";

  useEffect(() => {
    setFormData({
      tipo: "disponivel",
      cliente_id: "",
      funcionario_id: "",
      ordem_servico_id: "",
      localizacao_descricao: "",
      responsavel_id: "",
      solicitante_id: "",
      recebedor_id: "",
      emprestado_para: "",
      motivo: "",
      data_previsao_retorno: ""
    });
  }, [open]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (requiresCliente && !formData.cliente_id) {
      toast.error("Selecione o cliente");
      return;
    }
    if (requiresFuncionarioDestino && !formData.funcionario_id) {
      toast.error("Selecione o funcionário que ficará com o item");
      return;
    }
    if (requiresEmprestado && !formData.emprestado_para) {
      toast.error("Informe para quem está emprestando");
      return;
    }
    if (isPrejuizo && !formData.motivo) {
      toast.error("Informe o motivo do prejuízo");
      return;
    }

    setSaving(true);
    try {
      const token = sessionStorage.getItem("luvcorp_token");
      const res = await axios.post(`${API}/patrimonio/movimentar`, {
        patrimonio_id: item.id,
        tipo: formData.tipo,
        cliente_id: formData.cliente_id || null,
        funcionario_id: formData.funcionario_id || null,
        ordem_servico_id: formData.ordem_servico_id || null,
        localizacao_descricao: formData.localizacao_descricao || null,
        responsavel_id: formData.responsavel_id || null,
        solicitante_id: formData.solicitante_id || null,
        recebedor_id: formData.recebedor_id || null,
        recebedor_nome_livre: formData.recebedor_nome_livre || null,
        emprestado_para: formData.emprestado_para || null,
        motivo: formData.motivo || null,
        data_previsao_retorno: formData.data_previsao_retorno || null,
        gerar_prejuizo: isPrejuizo
      }, { headers: { Authorization: `Bearer ${token}` } });
      
      if (res.data.prejuizo_registrado) {
        toast.success(`Movimentação registrada. Prejuízo de ${formatCurrency(res.data.prejuizo_registrado.valor)} lançado.`);
      } else {
        toast.success("Movimentação registrada com sucesso");
      }
      onSaved();
      onClose();
    } catch (err) {
      toast.error(extractErrorMessage(err, "Erro ao registrar"));
    } finally {
      setSaving(false);
    }
  };

  // Filtrar OS pelo cliente selecionado
  const osFiltered = formData.cliente_id 
    ? ordensServico.filter(os => os.client_id === formData.cliente_id)
    : ordensServico;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Movimentar Patrimônio</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="p-3 bg-slate-50 rounded-lg">
            <div className="flex justify-between">
              <div>
                <p className="text-sm text-slate-500">Item</p>
                <p className="font-medium">{item?.nome}</p>
                <p className="text-sm font-mono text-slate-500">{item?.codigo_patrimonio}</p>
              </div>
              <div className="text-right">
                <p className="text-sm text-slate-500">Situação atual</p>
                <Badge className={situacaoConfig[item?.situacao]?.class}>
                  {situacaoConfig[item?.situacao]?.label}
                </Badge>
              </div>
            </div>
          </div>
          
          <div className="space-y-2">
            <Label>Nova Situação *</Label>
            <NativeSelect
              value={formData.tipo}
              onValueChange={(v) => setFormData({...formData, tipo: v, cliente_id: "", funcionario_id: "", ordem_servico_id: "", emprestado_para: ""})}
              options={situacoesDisponiveis.map(s => ({ value: s.value, label: `${s.icon} ${s.label}` }))}
            />
          </div>

          {isPrejuizo && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
              <div className="flex items-center gap-2 text-red-700">
                <AlertTriangle className="h-5 w-5" />
                <span className="font-medium">Atenção: Esta ação irá gerar prejuízo de {formatCurrency(item?.valor_aquisicao)}</span>
              </div>
              <p className="text-sm text-red-600 mt-1">O valor será lançado como despesa de prejuízo patrimonial.</p>
            </div>
          )}

          {requiresCliente && (
            <>
              <div className="space-y-2">
                <Label>Cliente *</Label>
                <NativeSelect
                  value={formData.cliente_id}
                  onValueChange={(v) => setFormData({...formData, cliente_id: v, ordem_servico_id: ""})}
                  placeholder="Selecione o cliente"
                  options={clientes.map(c => ({ value: c.id, label: c.nome }))}
                />
              </div>
              {formData.cliente_id && osFiltered.length > 0 && (
                <div className="space-y-2">
                  <Label>Ordem de Serviço (opcional)</Label>
                  <NativeSelect
                    value={formData.ordem_servico_id}
                    onValueChange={(v) => setFormData({...formData, ordem_servico_id: v})}
                    placeholder="Selecione a OS"
                    options={[{ value: "", label: "Nenhuma" }, ...osFiltered.map(os => ({ value: os.id, label: `${os.numero || os.title}` }))]}
                  />
                </div>
              )}
            </>
          )}

          {requiresFuncionarioDestino && (
            <div className="space-y-2">
              <Label>Funcionário (destinatário) *</Label>
              <NativeSelect
                data-testid="mov-funcionario-destino-select"
                value={formData.funcionario_id}
                onValueChange={(v) => setFormData({...formData, funcionario_id: v})}
                placeholder="Selecione o funcionário que ficará com o item"
                options={funcionarios.filter(f => f.ativo !== false).map(f => ({ value: f.id, label: `${f.nome}${f.cargo ? ` — ${f.cargo}` : ''}` }))}
              />
              <p className="text-xs text-slate-500">O item ficará sob responsabilidade deste funcionário (excludente com alocação a cliente).</p>
            </div>
          )}

          {requiresEmprestado && (
            <div className="space-y-2">
              <Label>Emprestado para *</Label>
              <Input 
                value={formData.emprestado_para} 
                onChange={(e) => setFormData({...formData, emprestado_para: e.target.value})}
                placeholder="Nome da pessoa ou empresa"
              />
            </div>
          )}

          <div className="space-y-2">
            <Label>Localização/Endereço</Label>
            <Input 
              value={formData.localizacao_descricao} 
              onChange={(e) => setFormData({...formData, localizacao_descricao: e.target.value})}
              placeholder="Ex: Condomínio Renoir, Bloco A, 3º andar"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Responsável (funcionário)</Label>
              <NativeSelect
                value={formData.responsavel_id}
                onValueChange={(v) => setFormData({...formData, responsavel_id: v})}
                placeholder="Selecione..."
                options={[{ value: "", label: "Nenhum" }, ...funcionarios.map(f => ({ value: f.id, label: f.nome }))]}
              />
            </div>
            <div className="space-y-2">
              <Label>Previsão de Retorno</Label>
              <Input 
                type="date"
                value={formData.data_previsao_retorno} 
                onChange={(e) => setFormData({...formData, data_previsao_retorno: e.target.value})}
              />
            </div>
          </div>

          {/* Solicitante & Recebedor (usuários do sistema) */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Solicitante (usuário do sistema)</Label>
              <NativeSelect
                value={formData.solicitante_id}
                onValueChange={(v) => setFormData({...formData, solicitante_id: v})}
                placeholder="Quem solicitou..."
                options={[{ value: "", label: "— Nenhum —" }, ...usuarios.map(u => ({ value: u.id, label: u.name }))]}
                data-testid="select-solicitante"
              />
            </div>
            <div className="space-y-2">
              <Label>Recebedor (usuário, funcionário ou nome livre)</Label>
              <NativeSelect
                value={formData.recebedor_id}
                onValueChange={(v) => setFormData({...formData, recebedor_id: v})}
                placeholder="Quem recebeu no destino..."
                options={[{ value: "", label: "— Nenhum —" }, ...usuarios.map(u => ({ value: u.id, label: u.name }))]}
                data-testid="select-recebedor"
              />
              <Input
                value={formData.recebedor_nome_livre || ""}
                onChange={(e) => setFormData({...formData, recebedor_nome_livre: e.target.value})}
                placeholder="…ou escreva o nome de quem recebeu (sem login)"
                data-testid="input-recebedor-nome-livre"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label>{isPrejuizo ? "Motivo do Prejuízo *" : "Motivo/Observação"}</Label>
            <Textarea 
              value={formData.motivo} 
              onChange={(e) => setFormData({...formData, motivo: e.target.value})}
              placeholder={isPrejuizo ? "Descreva as circunstâncias..." : "Observações sobre a movimentação"}
              rows={2}
            />
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Cancelar</Button>
            <Button type="submit" disabled={saving} className={isPrejuizo ? "bg-red-600 hover:bg-red-700" : "bg-luvcorp-navy hover:bg-luvcorp-navy-dark"}>
              {saving ? "Salvando..." : isPrejuizo ? "Registrar Prejuízo" : "Confirmar"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ==================== REPARO DIALOG (Patrimônio v2) ====================
function ReparoDialog({ open, onClose, item, fornecedores = [], usuarios = [], onSaved }) {
  const [saving, setSaving] = useState(false);
  const today = new Date().toISOString().slice(0, 10);
  const [data, setData] = useState({
    descricao: "",
    data_inicio: today,
    data_fim: "",
    custo: 0,
    fornecedor_id: "",
    responsavel_id: "",
    observacoes: "",
  });

  useEffect(() => {
    if (open) {
      setData({
        descricao: "",
        data_inicio: new Date().toISOString().slice(0, 10),
        data_fim: "",
        custo: 0,
        fornecedor_id: "",
        responsavel_id: "",
        observacoes: "",
      });
    }
  }, [open]);

  const submit = async () => {
    if (!data.descricao.trim()) { toast.error("Descrição é obrigatória"); return; }
    if (!data.data_inicio) { toast.error("Data início é obrigatória"); return; }
    if (data.data_fim && data.data_fim < data.data_inicio) { toast.error("Data fim não pode ser anterior à data início"); return; }
    if ((parseFloat(data.custo) || 0) < 0) { toast.error("Custo não pode ser negativo"); return; }

    setSaving(true);
    try {
      const token = sessionStorage.getItem("luvcorp_token");
      await axios.post(`${API}/patrimonio/${item.id}/reparo`, {
        patrimonio_id: item.id,
        descricao: data.descricao,
        data_inicio: data.data_inicio,
        data_fim: data.data_fim || null,
        custo: parseFloat(data.custo) || 0,
        fornecedor_id: data.fornecedor_id || null,
        responsavel_id: data.responsavel_id || null,
        observacoes: data.observacoes || null,
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Reparo registrado${parseFloat(data.custo) > 0 ? " (lançamento financeiro criado)" : ""}`);
      onSaved();
      onClose();
    } catch (err) {
      toast.error(extractErrorMessage(err, "Erro ao registrar reparo"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="dialog-reparo">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-amber-700">
            <Wrench className="h-5 w-5" /> Registrar Reparo
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3 text-sm">
          {item && (
            <div className="bg-slate-50 p-2 rounded text-xs border">
              <b>{item.codigo_patrimonio}</b> — {item.nome}
            </div>
          )}
          <div>
            <Label>Descrição do reparo *</Label>
            <Textarea
              value={data.descricao}
              onChange={(e) => setData({ ...data, descricao: e.target.value })}
              placeholder="Ex: Troca de motor, calibragem, etc."
              rows={2}
              data-testid="input-reparo-descricao"
            />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label>Data início *</Label>
              <Input
                type="date"
                value={data.data_inicio}
                onChange={(e) => setData({ ...data, data_inicio: e.target.value })}
                max={new Date().toISOString().slice(0, 10)}
              />
            </div>
            <div>
              <Label>Data fim</Label>
              <Input
                type="date"
                value={data.data_fim}
                onChange={(e) => setData({ ...data, data_fim: e.target.value })}
              />
              <p className="text-[10px] text-slate-500 mt-1">Sem data fim = ainda em andamento</p>
            </div>
            <div>
              <Label>Custo (R$)</Label>
              <Input
                type="number" min="0" step="0.01"
                value={data.custo}
                onChange={(e) => setData({ ...data, custo: e.target.value })}
                data-testid="input-reparo-custo"
              />
              {parseFloat(data.custo) > 0 && (
                <p className="text-[10px] text-emerald-600 mt-1">Gera lançamento financeiro automático</p>
              )}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Fornecedor</Label>
              <NativeSelect
                value={data.fornecedor_id}
                onValueChange={(v) => setData({ ...data, fornecedor_id: v })}
                placeholder="Quem executou o reparo..."
                options={[{ value: "", label: "— Nenhum —" }, ...fornecedores.map(f => ({ value: f.id, label: f.nome }))]}
              />
            </div>
            <div>
              <Label>Responsável (usuário)</Label>
              <NativeSelect
                value={data.responsavel_id}
                onValueChange={(v) => setData({ ...data, responsavel_id: v })}
                placeholder="Quem acompanhou..."
                options={[{ value: "", label: "— Nenhum —" }, ...usuarios.map(u => ({ value: u.id, label: u.name }))]}
              />
            </div>
          </div>
          <div>
            <Label>Observações</Label>
            <Textarea
              value={data.observacoes}
              onChange={(e) => setData({ ...data, observacoes: e.target.value })}
              rows={2}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancelar</Button>
          <Button onClick={submit} disabled={saving} className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="btn-salvar-reparo">
            {saving ? "Salvando..." : "Registrar Reparo"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ==================== DELETE / INATIVAR Dialogs (Patrimônio v2) ====================
function DeletePatrimonioDialog({ item, onClose, onDeleted }) {
  const [confirming, setConfirming] = useState(false);
  const [adminPassword, setAdminPassword] = useState("");
  const [showForce, setShowForce] = useState(false);

  if (!item) return null;
  const podeExcluirSimples = ["disponivel", "baixado"].includes(item.situacao);
  const headers = () => ({ Authorization: `Bearer ${sessionStorage.getItem("luvcorp_token")}` });

  const doDelete = async (force) => {
    setConfirming(true);
    try {
      const params = force ? `?force=true&admin_password=${encodeURIComponent(adminPassword)}` : "";
      await axios.delete(`${API}/patrimonio/${item.id}${params}`, { headers: headers() });
      toast.success(force ? "Item excluído (forçado)" : "Item excluído");
      onDeleted();
      onClose();
      setAdminPassword("");
      setShowForce(false);
    } catch (err) {
      toast.error(extractErrorMessage(err, "Erro ao excluir"));
    } finally {
      setConfirming(false);
    }
  };

  return (
    <Dialog open={!!item} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="dialog-delete-patrimonio">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-red-700">
            <Trash2 className="h-5 w-5" /> Excluir patrimônio
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3 text-sm">
          <div className="bg-slate-50 p-3 rounded border">
            <p className="font-semibold">{item.codigo_patrimonio} — {item.nome}</p>
            <div className="text-xs text-slate-500 mt-1 flex items-center gap-1">
              <span>Situação atual:</span>
              <Badge className={situacaoConfig[item.situacao]?.class || ""}>{situacaoConfig[item.situacao]?.label || item.situacao}</Badge>
            </div>
          </div>

          {podeExcluirSimples ? (
            <p className="text-slate-700">
              Esta ação fará <b>soft-delete</b> do item. O histórico de movimentações e reparos será arquivado mas não perdido. Continuar?
            </p>
          ) : (
            <>
              <div className="bg-amber-50 border border-amber-200 p-3 rounded text-amber-900">
                <p className="font-medium flex items-start gap-2">
                  <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                  Item em uso — não pode ser excluído normalmente.
                </p>
                <p className="text-xs mt-1">
                  <b>Sugestão:</b> devolva o item ao depósito (situação {'"Disponível"'}) ou faça a baixa antes.
                </p>
              </div>
              {!showForce && (
                <Button variant="outline" className="w-full" onClick={() => setShowForce(true)} data-testid="btn-mostrar-force">
                  <AlertOctagon className="h-4 w-4 mr-2 text-red-500" />
                  Forçar exclusão (requer senha do admin)
                </Button>
              )}
              {showForce && (
                <div className="space-y-2 border-l-4 border-red-500 pl-3 bg-red-50/50 py-2 rounded-r">
                  <p className="text-xs text-red-800 font-medium">
                    ⚠ Exclusão forçada é registrada na auditoria. Confirme com sua senha de administrador.
                  </p>
                  <Input
                    type="password"
                    placeholder="Senha do administrador"
                    value={adminPassword}
                    onChange={(e) => setAdminPassword(e.target.value)}
                    data-testid="input-admin-password"
                    autoFocus
                  />
                </div>
              )}
            </>
          )}
        </div>
        <DialogFooter className="flex-row sm:justify-between">
          <Button variant="outline" onClick={onClose} disabled={confirming}>Cancelar</Button>
          {podeExcluirSimples ? (
            <Button onClick={() => doDelete(false)} disabled={confirming} className="bg-red-600 hover:bg-red-700 text-white" data-testid="btn-confirmar-delete">
              {confirming ? "Excluindo..." : "Confirmar exclusão"}
            </Button>
          ) : showForce ? (
            <Button onClick={() => doDelete(true)} disabled={confirming || !adminPassword} className="bg-red-700 hover:bg-red-800 text-white" data-testid="btn-confirmar-force">
              {confirming ? "Excluindo..." : "Forçar exclusão"}
            </Button>
          ) : null}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function InativarPatrimonioDialog({ item, onClose, onSaved }) {
  const [motivo, setMotivo] = useState("");
  const [obs, setObs] = useState("");
  const [saving, setSaving] = useState(false);

  if (!item) return null;
  const headers = () => ({ Authorization: `Bearer ${sessionStorage.getItem("luvcorp_token")}` });

  const submit = async () => {
    if (!motivo.trim()) { toast.error("Motivo é obrigatório"); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/patrimonio/${item.id}/inativar`, { motivo, observacoes: obs }, { headers: headers() });
      toast.success("Patrimônio inativado");
      onSaved();
      onClose();
      setMotivo(""); setObs("");
    } catch (err) {
      toast.error(extractErrorMessage(err, "Erro ao inativar"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={!!item} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="dialog-inativar-patrimonio">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-amber-700">
            <AlertOctagon className="h-5 w-5" /> Inativar patrimônio
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3 text-sm">
          <div className="bg-slate-50 p-3 rounded border">
            <p className="font-semibold">{item.codigo_patrimonio} — {item.nome}</p>
          </div>
          <p className="text-xs text-slate-600">
            Patrimônios inativos não aparecem em dropdowns de movimentação, mas o histórico permanece consultável.
            Você pode <b>reativar</b> depois.
          </p>
          <div>
            <Label>Motivo *</Label>
            <Input
              value={motivo}
              onChange={(e) => setMotivo(e.target.value)}
              placeholder="Ex: Final de vida útil, obsoleto, etc."
              data-testid="input-motivo-inativacao"
            />
          </div>
          <div>
            <Label>Observações</Label>
            <Textarea value={obs} onChange={(e) => setObs(e.target.value)} rows={2} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancelar</Button>
          <Button onClick={submit} disabled={saving || !motivo.trim()} className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="btn-confirmar-inativar">
            {saving ? "Inativando..." : "Confirmar inativação"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ==================== RELATÓRIO POR CLIENTE ====================
function RelatorioClienteDialog({ open, onClose, clientes }) {
  const [clienteId, setClienteId] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => { if (open) setClienteId(""); }, [open]);

  const handleGenerate = async () => {
    if (!clienteId) return toast.error("Selecione um cliente");
    setLoading(true);
    try {
      const token = sessionStorage.getItem("luvcorp_token");
      const headers = { Authorization: `Bearer ${token}` };
      const cliente = clientes.find(c => c.id === clienteId);
      const [itemsRes, movsRes] = await Promise.all([
        axios.get(`${API}/patrimonio/por-cliente/${clienteId}`, { headers }),
        axios.get(`${API}/patrimonio/movimentacoes?cliente_id=${clienteId}&limit=200`, { headers }).catch(() => ({ data: [] })),
      ]);
      toast.info("Gerando PDF...");
      await gerarPDFPatrimonioPorCliente({
        cliente,
        items: itemsRes.data || [],
        movimentacoes: movsRes.data || [],
      });
      toast.success("PDF gerado");
      onClose();
    } catch (err) {
      toast.error("Erro ao gerar PDF: " + extractErrorMessage(err));
    } finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent data-testid="dialog-relatorio-cliente">
        <DialogHeader>
          <DialogTitle>Relatório de Patrimônio por Cliente</DialogTitle>
          <DialogDescription>Lista todos os itens atualmente alocados a um cliente + histórico de movimentações</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <Label>Cliente *</Label>
          <NativeSelect
            data-testid="rel-cliente-select"
            value={clienteId}
            onValueChange={setClienteId}
            placeholder="Selecione o cliente"
            options={clientes.map(c => ({ value: c.id, label: c.nome }))}
          />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={loading}>Cancelar</Button>
          <Button onClick={handleGenerate} disabled={loading || !clienteId} className="bg-luvcorp-navy hover:bg-luvcorp-navy-dark text-white" data-testid="btn-gerar-rel-cliente">
            {loading ? "Gerando..." : "Gerar PDF"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ==================== RELATÓRIO POR FUNCIONÁRIO ====================
function RelatorioFuncionarioDialog({ open, onClose, funcionarios }) {
  const [funcId, setFuncId] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => { if (open) setFuncId(""); }, [open]);

  const handleGenerate = async () => {
    if (!funcId) return toast.error("Selecione um funcionário");
    setLoading(true);
    try {
      const token = sessionStorage.getItem("luvcorp_token");
      const headers = { Authorization: `Bearer ${token}` };
      const [dataRes, movsRes] = await Promise.all([
        axios.get(`${API}/patrimonio/por-funcionario/${funcId}`, { headers }),
        axios.get(`${API}/patrimonio/movimentacoes?funcionario_id=${funcId}&limit=200`, { headers }).catch(() => ({ data: [] })),
      ]);
      toast.info("Gerando PDF...");
      await gerarPDFPatrimonioPorFuncionario({
        funcionario: dataRes.data.funcionario,
        items: dataRes.data.items || [],
        movimentacoes: movsRes.data || [],
      });
      toast.success("PDF gerado");
      onClose();
    } catch (err) {
      toast.error("Erro ao gerar PDF: " + extractErrorMessage(err));
    } finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent data-testid="dialog-relatorio-funcionario">
        <DialogHeader>
          <DialogTitle>Relatório de Patrimônio por Funcionário</DialogTitle>
          <DialogDescription>Lista todos os itens sob responsabilidade de um funcionário + histórico</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <Label>Funcionário *</Label>
          <NativeSelect
            data-testid="rel-funcionario-select"
            value={funcId}
            onValueChange={setFuncId}
            placeholder="Selecione o funcionário"
            options={funcionarios.filter(f => f.ativo !== false).map(f => ({ value: f.id, label: `${f.nome}${f.cargo ? ` — ${f.cargo}` : ''}` }))}
          />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={loading}>Cancelar</Button>
          <Button onClick={handleGenerate} disabled={loading || !funcId} className="bg-luvcorp-navy hover:bg-luvcorp-navy-dark text-white" data-testid="btn-gerar-rel-funcionario">
            {loading ? "Gerando..." : "Gerar PDF"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ==================== MAIN ====================
export default function PatrimonioPage() {
  return (
    <Routes>
      <Route index element={<PatrimonioList />} />
      <Route path=":id" element={<PatrimonioView />} />
    </Routes>
  );
}
